import config from '@/localconfig';

export const REGIONS = [
  { code: 'en-US', prefix: '',     label: 'United States',    isDefault: true },
  { code: 'en-GB', prefix: '/uk',  label: 'United Kingdom' },
  { code: 'en-CA', prefix: '/ca',  label: 'Canada' },
  { code: 'en-AU', prefix: '/au',  label: 'Australia' },
  { code: 'es-ES', prefix: '/es',  label: 'Spain' },
  { code: 'fr-FR', prefix: '/fr',  label: 'France' },
  { code: 'de-DE', prefix: '/de',  label: 'Germany' },
  { code: 'zh-Hans', prefix: '/zh', label: 'China (Mandarin)' },
];

export const DEFAULT_REGION = REGIONS.find((r) => r.isDefault);

/**
 * Builds the `alternates.languages` object expected by the Next.js Metadata API.
 *
 * @param {string} canonicalUrl - Absolute canonical URL as stored in the CMS
 *   (e.g. "https://www.nettleworth.com/net-worth/men/24-years-old")
 *   or a root-relative path (e.g. "/net-worth/men/24-years-old").
 * @returns {Record<string, string>} Map of locale code → absolute URL, including 'x-default'.
 *
 * @example
 * generateHreflangAlternates('https://www.nettleworth.com/net-worth/men/24-years-old')
 * // {
 * //   'en-US':   'https://www.nettleworth.com/net-worth/men/24-years-old',
 * //   'en-GB':   'https://www.nettleworth.com/uk/net-worth/men/24-years-old',
 * //   'x-default': 'https://www.nettleworth.com/net-worth/men/24-years-old',
 * //   ...
 * // }
 */
export function generateHreflangAlternates(canonicalUrl) {
  // Extract the path, whether input is absolute or relative.
  let path;
  try {
    path = new URL(canonicalUrl).pathname;
  } catch {
    // Not a valid absolute URL — treat the whole value as a path.
    path = canonicalUrl ?? '/';
  }

  // Strip query string and fragment (hreflang tags must not contain them).
  path = path.split('?')[0].split('#')[0];

  // Normalise trailing slash: keep it only on the root path so that
  // region prefixes compose cleanly (e.g. /uk + /net-worth, not /uk//net-worth).
  if (path !== '/' && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  // Ensure path starts with a slash.
  if (!path.startsWith('/')) {
    path = '/' + path;
  }

  const base = config.BASE_URL.replace(/\/$/, '');

  const alternates = {};

  for (const region of REGIONS) {
    // Root path with prefix: /uk + / → /uk (not /uk/ which triggers a 308)
    const urlPath = path === '/' && region.prefix
      ? region.prefix
      : `${region.prefix}${path}`;
    alternates[region.code] = `${base}${urlPath}`;
  }

  // x-default → canonical/default region (en-US, no prefix).
  alternates['x-default'] = `${base}${path}`;

  return alternates;
}

/**
 * Generates a self-referential canonical URL for the given locale.
 *
 * Each locale page should canonical to itself (not to en-US).
 *
 * @param {string} basePath - Root-relative path (e.g. "/about", "/net-worth/men/24-years-old").
 * @param {string} locale   - Locale code (e.g. "de-DE", "en-US").
 * @returns {string} Absolute canonical URL for the given locale.
 *
 * @example
 * generateSelfCanonical('/about', 'de-DE')
 * // → 'https://www.nettleworth.com/de/about'
 *
 * generateSelfCanonical('/about', 'en-US')
 * // → 'https://www.nettleworth.com/about'
 */
export function generateSelfCanonical(basePath, locale) {
  const base = config.BASE_URL.replace(/\/$/, '');

  // Find the region; fall back to default if locale is missing/invalid.
  const region = REGIONS.find((r) => r.code === locale) || DEFAULT_REGION;

  // Normalise basePath.
  let path = basePath ?? '/';
  if (path === '') path = '/';
  if (!path.startsWith('/')) path = '/' + path;
  if (path !== '/' && path.endsWith('/')) path = path.slice(0, -1);

  // For root path with a prefix: /de + / → /de (not /de/)
  if (path === '/' && region.prefix) {
    return `${base}${region.prefix}`;
  }

  return `${base}${region.prefix}${path}`;
}
